import { useAnalytics } from "@codat/analytics";
import {
  Modal,
  Divider as OrchardDivider,
  Skeleton,
  TextArea,
  useToast,
} from "@codat/orchard-ui";
import {
  useSignInMessageMutation,
  useSignInMessageQuery,
} from "api/signinmessages";
import { ClientLoginSetting } from "api/users";
import { AxiosError } from "axios";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { ApiError } from "shared";
import { useDebouncedCallback } from "use-debounce";

import { useUserManagementService } from "../UserManagementService";
import styles from "./ManageLogin.module.css";
import { mapClientLoginSettings } from "./mapClientLoginSettings";
import SettingsRow from "./SettingsRow";

export interface ManageLoginModalProps {
  open: boolean;
  onClose: () => void;
}

const Divider = () => <OrchardDivider className={styles.divider} />;

const ManageLoginModal: React.FC<ManageLoginModalProps> = ({
  open,
  onClose,
}) => {
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();

  const userManagementService = useUserManagementService();
  const [data, setData] = useState<ClientLoginSetting[]>([]);

  const [, putSignInMessage] = useSignInMessageMutation();

  const [, getSignInMessage] = useSignInMessageQuery();
  const [loadingSignin, setLoadingSignin] = useState(true);

  const [messageText, setMessageText] = useState("");

  const handleMessageUpdate = useCallback(
    (changeEvent: ChangeEvent<HTMLTextAreaElement>) => {
      setMessageText(changeEvent.target.value);
    },
    [setMessageText]
  );

  const debouncedMessageUpdate = useDebouncedCallback((newMsg: string) => {
    if (!loadingSignin) {
      putSignInMessage({ data: { message: newMsg } });
    }
  }, 500);

  useEffect(() => {
    debouncedMessageUpdate(messageText);
  }, [messageText, debouncedMessageUpdate]);

  if (!userManagementService) {
    throw new Error("No User Management Service provider");
  }

  useEffect(() => {
    if (open) {
      getSignInMessage().then((response) => {
        setMessageText(response.data.message);
        setLoadingSignin(false);
      });
    }
  }, [open, getSignInMessage]);

  useEffect(() => {
    const controller = new AbortController();
    const getData = userManagementService.getLoginSettings.get({
      signal: controller.signal,
    });
    getData
      .then((response) => {
        if (open) {
          setData(response.data);
        }
      })
      .catch((error: AxiosError<ApiError>) => {
        if (controller.signal.aborted) {
          return;
        }
        const errorMessage = error.response?.data?.error ?? "Unknown error";
        addToast({
          message: `Unable to load: ${errorMessage}`,
          variant: "error",
        });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      size="small"
      data-testid="manage-client-logins-modal"
      onClose={async () => {
        await putSignInMessage({ data: { message: messageText } });

        sendEvent("Close");
        onClose();
      }}
    >
      {open && (
        <>
          <Modal.Header
            title="Manage sign-in"
            onClose={() => {
              putSignInMessage({ data: { message: messageText } }).finally(
                () => {
                  sendEvent("Close Icon - Click");
                  onClose();
                }
              );
            }}
          />
          <Modal.Body variant="dark">
            <Modal.Section variant="card" title="Sign in method">
              {userManagementService.getLoginSettings.isLoading() ? (
                <>
                  <Skeleton height="1.5em" width="100%" />
                  <Divider />
                  <Skeleton height="1.5em" width="100%" />
                  <Divider />
                  <Skeleton height="1.5em" width="100%" />
                </>
              ) : (
                data &&
                mapClientLoginSettings(data).map((setting) => (
                  <React.Fragment key={setting.typeId}>
                    <div
                      className={styles.settingsRow}
                      data-testid={`manage-client-logins-${setting.name.replace(
                        " ",
                        "-"
                      )}`}
                    >
                      <SettingsRow
                        setting={setting}
                        addToast={addToast}
                        onToggle={
                          userManagementService.setLoginSetting.setLoginSetting
                        }
                      />
                    </div>
                    <Divider />
                  </React.Fragment>
                ))
              )}
            </Modal.Section>
            <Modal.Section variant="card" title="Sign in message">
              <TextArea
                id="text-area"
                placeholder={loadingSignin ? undefined : "Custom message"}
                showLabel={false}
                label=""
                maxLength={250}
                showCharacterCount
                isSkeleton={loadingSignin}
                value={messageText}
                onChange={handleMessageUpdate}
              />
            </Modal.Section>
          </Modal.Body>
          <Modal.Footer
            variant="light"
            buttons={[
              {
                label: "Close",
                variant: "primary",
                className: styles.closeButton,
                onClick: onClose,
                id: "close-button",
                "data-testid": "close-button",
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
};
export default ManageLoginModal;
